import { useCallback, useMemo } from 'react';

import { useCursorPagination } from '@liminal/web-ui/primitives/useCursorPagination';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import { useDebounce } from '@/hooks/useDebounce';
import { useUrlState } from '@/hooks/useUrlState';
import {
  DEFAULT_CHAIN_ID,
  useUserActivity,
  useUserWalletTransactions,
} from '@/hooks/useUsers';
import type { Transaction, WalletTransaction } from '@/api/types';

/**
 * Rows per page, for both tables. The Previous/Next controls step by this, so a
 * fetch size larger than the step would show rows the step then skips over.
 * The ledger caps page_size at 100 server-side; the wallets service at 1000.
 */
export const TRANSACTIONS_PAGE_SIZE = 25;

/**
 * Both panels share the user detail URL, so the wallet keys carry a prefix
 * to keep its status and offset apart from the ledger's.
 */
const LEDGER_DEFAULTS = { context: '', status: '', asset: '' };
const WALLET_DEFAULTS = { chain: DEFAULT_CHAIN_ID, chainStatus: '', chainOffset: 0 };

/**
 * State, filters and paging for the two tables on a user's Transactions tab.
 *
 * Both endpoints already accepted these filters — the tab simply never sent
 * them, and asked for a single fixed page — so this is wiring, not new API
 * surface. They are split into two hooks because the two services page
 * differently and that difference reaches the screen: the ledger hands back an
 * opaque cursor, so the only honest controls are Previous/Next over a stack of
 * visited cursors; the wallets service takes limit/offset, so its table steps
 * an offset. Neither reports a total (AIP-158), so a page shorter than the
 * requested size is the only end-of-results signal either table has.
 */

/**
 * Both tables sort the page on screen: the ledger orders by recency and the
 * wallets service by chain order, and neither takes a sort parameter — so a
 * click reorders the 25 rows fetched, not the user's whole history.
 *
 * Entries and Metadata have no accessors on purpose. Each renders several
 * values stacked in one cell, so there is no single figure to order by.
 */
const LEDGER_SORT_ACCESSORS = {
  id: (tx: Transaction) => tx.id,
  context: (tx: Transaction) => tx.context,
  status: (tx: Transaction) => tx.status,
  note: (tx: Transaction) => tx.note,
  createdAt: (tx: Transaction) => tx.createdAt,
  updatedAt: (tx: Transaction) => tx.updatedAt,
  confirmedAt: (tx: Transaction) => tx.confirmedAt,
};

const WALLET_SORT_ACCESSORS = {
  transactionHash: (tx: WalletTransaction) => tx.transactionHash,
  userOperationHash: (tx: WalletTransaction) => tx.userOperationHash,
  tokenSymbol: (tx: WalletTransaction) => tx.tokenSymbol,
  // The raw decimal string, so the comparator can order it exactly.
  amountHuman: (tx: WalletTransaction) => tx.amountHuman,
  recipientAddress: (tx: WalletTransaction) => tx.recipientAddress,
  status: (tx: WalletTransaction) => tx.status,
  createdAt: (tx: WalletTransaction) => tx.createdAt,
  errorMessage: (tx: WalletTransaction) => tx.errorMessage,
};

/** Ledger transactions: server-side context/status/asset filters + cursor paging. */
export function useLedgerTransactionsPanel(userId: string) {
  const [{ context, status, asset: assetSymbol }, setParams] = useUrlState(LEDGER_DEFAULTS);

  const pagination = useCursorPagination(TRANSACTIONS_PAGE_SIZE);
  const { reset: resetPaging } = pagination;

  const debouncedAssetSymbol = useDebounce(assetSymbol, 300);

  const { data, isLoading, isError } = useUserActivity(userId, {
    context: context || undefined,
    status: status || undefined,
    // The ledger compares asset_symbol with `=`, not ILIKE, so a typed "usdc"
    // would quietly match nothing. Upper-casing here is the difference between
    // an empty table and the user's USDC history.
    assetSymbol: debouncedAssetSymbol.trim().toUpperCase() || undefined,
    pageSize: TRANSACTIONS_PAGE_SIZE,
    pageToken: pagination.pageToken,
  });

  const fetched: Transaction[] = useMemo(() => data?.transactions ?? [], [data]);
  const nextPageToken = data?.nextPageToken ?? '';

  const { rows: transactions, sort } = useTableSort(
    fetched,
    LEDGER_SORT_ACCESSORS,
    'createdAt',
    'desc',
  );

  // Dedicated setters rather than the raw ones, so no caller can change a
  // filter and leave a stale page-4 cursor pointing into the old result set.
  const setContext = useCallback((value: string) => {
    setParams({ context: value });
    resetPaging();
  }, [setParams, resetPaging]);

  const setStatus = useCallback((value: string) => {
    setParams({ status: value });
    resetPaging();
  }, [setParams, resetPaging]);

  const setAssetSymbol = useCallback((value: string) => {
    setParams({ asset: value });
    resetPaging();
  }, [setParams, resetPaging]);

  // There is no "of N" to show: the ledger would have to count the whole
  // filtered set to produce one.
  const { firstRowNumber } = pagination;
  const lastRowNumber = firstRowNumber + transactions.length - 1;

  return {
    context,
    status,
    assetSymbol,
    setContext,
    setStatus,
    setAssetSymbol,
    transactions,
    sort,
    isLoading,
    isError,
    pageIndex: pagination.pageIndex,
    firstRowNumber,
    lastRowNumber,
    hasPreviousPage: pagination.hasPreviousPage,
    hasNextPage: nextPageToken !== '',
    goToPreviousPage: pagination.goToPreviousPage,
    goToNextPage: () => pagination.goToNextPage(nextPageToken),
  };
}

/** On-chain transactions: server-side chain/status filters + offset paging. */
export function useWalletTransactionsPanel(userId: string) {
  const [{ chain: chainId, chainStatus: status, chainOffset: offset }, setParams] =
    useUrlState(WALLET_DEFAULTS);

  const { data, isLoading, isError } = useUserWalletTransactions(userId, {
    chainId,
    status: status || undefined,
    limit: TRANSACTIONS_PAGE_SIZE,
    offset,
  });

  const fetched: WalletTransaction[] = useMemo(() => data?.transactions ?? [], [data]);

  // No default column: the wallets service does not document its order, so the
  // table shows the response as it arrived until a header is clicked.
  const { rows: transactions, sort } = useTableSort(fetched, WALLET_SORT_ACCESSORS);

  const setChainId = useCallback((value: number) => {
    setParams({ chain: value, chainOffset: 0 });
  }, [setParams]);

  const setStatus = useCallback((value: string) => {
    setParams({ chainStatus: value, chainOffset: 0 });
  }, [setParams]);

  const setOffset = useCallback((value: number) => {
    setParams({ chainOffset: value });
  }, [setParams]);

  return {
    chainId,
    status,
    setChainId,
    setStatus,
    transactions,
    sort,
    isLoading,
    isError,
    offset,
    // @liminal/web-ui/primitives/Pagination steps the offset itself; the panel just holds it.
    setOffset,
  };
}
